import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// इसमें faRightFromBracket add करो: done with logic from anti
import { faUser, faXmark, faGear, faCreditCard, faShield, faCircleUser, faUsers, faFolderTree, faRightFromBracket, faChartBar } from "@fortawesome/free-solid-svg-icons";
import { redirectToCheckout } from "../lib/stripe";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";
const BILLING_CURRENCY = (process.env.REACT_APP_BILLING_CURRENCY || "USD").toUpperCase();

// Money amounts are stored in the smallest currency unit (cents for USD,
// paise for INR). One helper formats whatever currency the doc says.
const fmtMoney = (smallestUnit?: number | null, currency?: string) => {
  if (smallestUnit == null) return "—";
  const cur = (currency || BILLING_CURRENCY || "USD").toUpperCase();
  const n = smallestUnit / 100;
  try {
    return n.toLocaleString(undefined, { style: "currency", currency: cur, maximumFractionDigits: 2 });
  } catch {
    return `${cur} ${n.toFixed(2)}`;
  }
};
// Backward-compat alias used in many places below
const fmtRupees = fmtMoney;

// Module-scope so every component (BillingTab, BuyPlanDialog, etc.) can
// share the same Firestore-timestamp-aware formatter.
const fmtDate = (val: any) => {
  if (!val) return "—";
  try {
    let ms: number;
    if (typeof val === "number")        ms = val;
    else if (typeof val === "string")   ms = Date.parse(val);
    else if (val?.seconds  != null)     ms = val.seconds  * 1000;
    else if (val?._seconds != null)     ms = val._seconds * 1000;
    else if (val instanceof Date)       ms = val.getTime();
    else                                ms = NaN;
    if (!Number.isFinite(ms)) return "—";
    return new Date(ms).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};

// by devesh for log out and also profile
const ProfileDropdown: React.FC<{
  onLogout?: () => void;
  userEmail?: string;
  userName?: string;
  organisation?: string;
  userNumber?: string;
  status?: string;
  toolAccess?: boolean;
  ticketAccess?: boolean;
  quotaPerPeriod?: number;
  quotaRenewedAt?: any;
  subscriptionId?: string | null;
  lastLogin?: any;
  memberSince?: any;
  subscription?: any;
  plan?: any;
  wallet?: any;            // { total, by_source, buckets }
  usage?: any;             // token_usage doc
  creditBalanceCents?: number;   // Stripe customer credit balance (>0 = credit owed to user)
  creditCurrency?: string;
  paymentMethods?: any[];
  invoices?: any[];
  onRefreshBilling?: () => void; // re-fetch /api/billing/summary after a purchase
  // App-level toast trigger. Used by BuyPlan / cancel / resume flows so the
  // user gets the same green "Plan activated" banner after an upgrade /
  // downgrade as they do after the initial subscribe — keeps the success
  // signal consistent across all billing actions.
  onPaymentToast?: (toast: {
    kind: 'subscription' | 'addon' | 'cancelled' | 'failed';
    state: 'activating' | 'success' | 'cancelled' | 'failed';
    title: string;
    detail?: string;
  }) => void;
}> = ({
  onLogout, userEmail, userName, organisation, userNumber, status, toolAccess, ticketAccess,
  quotaPerPeriod, quotaRenewedAt, subscriptionId, lastLogin, memberSince,
  subscription, plan, wallet, usage, creditBalanceCents, creditCurrency,
  paymentMethods, invoices, onRefreshBilling, onPaymentToast,
}) => {
  // Derive display name: full name > email username > fallback
  const displayName = userName || (userEmail ? userEmail.split('@')[0] : 'User');
  const avatarLetter = displayName.charAt(0).toUpperCase();
  const [open, setOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [activeTab, setActiveTab] = useState("General");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      {/* Profile Icon */}
      <div ref={dropdownRef} style={{ position: "relative" }}>
        <div
          onClick={() => setOpen(!open)}
          style={{
            height: "36px",
            width: "36px",
            borderRadius: "50%",
            background: "#031735",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <span style={{ fontSize: "16px", fontWeight: 700, color: "white", lineHeight: 1 }}>
            {avatarLetter}
          </span>
        </div>

        {/* Dropdown */}
        {open && (
          <div
            style={{
              position: "absolute",
              right: 0,
              top: "50px",
              width: "220px",
              background: "white",
              borderRadius: "8px",
              boxShadow: "0 15px 35px rgba(0, 0, 0, 0.15), 0 5px 15px rgba(0, 0, 0, 0.1)",
              padding: "8px",
              zIndex: 1000,
              backdropFilter: "blur(10px)",
              border: "1px solid #d1d5db",
              animation: "fadeInDown 0.3s ease-out forwards",
              fontFamily: "monospace"
            }}
          >
            {/* User Info — Dropdown Top */}
            <div style={{ padding: "12px 12px 10px", borderBottom: "1px solid #d1d5db", marginBottom: "6px", display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{
                width: "40px", height: "40px", borderRadius: "50%",
                background: "#031735", display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0
              }}>
                <span style={{ fontSize: "18px", fontWeight: 700, color: "white", lineHeight: 1 }}>
                  {avatarLetter}
                </span>
              </div>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>{displayName}</div>
                <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>{organisation || "DevOpsX"}</div>
              </div>
            </div>

            <div
              style={itemStyle}
              onClick={() => {
                setShowProfile(true);
                setOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faCircleUser} style={{ fontSize: "16px", marginRight: "10px" }} />
              My Profile
            </div>
            <div style={itemStyle}>
              <FontAwesomeIcon icon={faGear} style={{ fontSize: "16px", marginRight: "10px" }} />
              Account Settings
            </div>
            <div
              style={itemStyle}
              onClick={() => {
                setActiveTab("Billing");
                setShowProfile(true);
                setOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: "16px", marginRight: "10px" }} />
              Billing
            </div>
            <div style={itemStyle}>
              <FontAwesomeIcon icon={faShield} style={{ fontSize: "16px", marginRight: "10px" }} />
              Help & Support
            </div>
            <hr style={{ margin: "8px 0", borderColor: "#d1d5db" }} />
            <div
              style={{ ...itemStyle, color: "#ef4444" }}
              onClick={() => { setOpen(false); if (onLogout) onLogout(); }}
            >
              <FontAwesomeIcon icon={faRightFromBracket} style={{ fontSize: "16px", marginRight: "10px" }} />
              Logout
            </div>
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {showProfile && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            {/* Sidebar */}
            <div style={sidebarStyle}>
              {/* Avatar + Name + Org */}
              <div style={{ marginBottom: "20px", padding: "0 12px", textAlign: "center" }}>
                <div style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  background: "#031735",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  boxShadow: "0 4px 15px rgba(3, 23, 53, 0.3)",
                }}>
                  <span style={{ fontSize: "26px", fontWeight: 400, color: "white", lineHeight: 1 }}>
                    {avatarLetter}
                  </span>
                </div>
                <h3 style={{ textAlign: "center", margin: "0 0 4px", fontSize: "16px", fontWeight: 400, color: "#1e293b" }}>
                  {displayName}
                </h3>
                <p style={{ textAlign: "center", margin: 0, fontSize: "13px", color: "#64748b" }}>
                  {organisation || "DevOpsX"}
                </p>
              </div>

              <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "16px", flex: 1 }}>
                {["General", "Billing", "Usage", "Account", "Security", "Preferences"].map((tab) => (
                  <div
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      ...tabStyle,
                      background:
                        activeTab === tab
                          ? "rgba(3, 23, 53, 0.08)"
                          : "transparent",
                      // color: activeTab === tab ?  : "#334155",
                      fontWeight: 400,
                      fontSize: "13px",
                      color: "#475569",
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: activeTab === tab ? "16px" : "12px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {tab === "General" && <FontAwesomeIcon icon={faCircleUser} style={{ fontSize: "14px", marginRight: "10px" }} />}
                    {tab === "Account" && <FontAwesomeIcon icon={faGear} style={{ fontSize: "14px", marginRight: "10px" }} />}
                    {tab === "Security" && <FontAwesomeIcon icon={faShield} style={{ fontSize: "14px", marginRight: "10px" }} />}
                    {tab === "Billing" && <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: "14px", marginRight: "10px" }} />}
                    {tab === "Preferences" && <FontAwesomeIcon icon={faGear} style={{ fontSize: "14px", marginRight: "10px" }} />}
                    {tab === "Usage" && <FontAwesomeIcon icon={faChartBar} style={{ fontSize: "14px", marginRight: "10px" }} />}
                    {tab}
                  </div>
                ))}
              </div>

              {/* Logout at bottom of sidebar */}
              <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "12px", marginTop: "16px" }}>
                <div
                  onClick={() => { setShowProfile(false); if (onLogout) onLogout(); }}
                  style={{
                    ...tabStyle,
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "12px",
                    color: "#ef4444",
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  <FontAwesomeIcon icon={faRightFromBracket} style={{ fontSize: "16px", marginRight: "10px" }} />
                  Logout
                </div>
              </div>
            </div>

            {/* Content */}
            <div style={contentStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <h2 style={{ margin: 0, fontSize: "24px", fontWeight: 700, color: "#0f172a" }}>{activeTab}</h2>
                <div
                  onClick={() => setShowProfile(false)}
                  style={
                    {
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                    }
                  }
                // onMouseEnter={(e) => {
                //   e.currentTarget.style.background = "#bfdbfe";
                //   e.currentTarget.style.transform = "rotate(90deg)";
                // }}
                // onMouseLeave={(e) => {
                //   e.currentTarget.style.background = "#dbeafe";
                //   e.currentTarget.style.transform = "rotate(0deg)";
                // }}
                >
                  <FontAwesomeIcon icon={faXmark} style={{ fontSize: "20px", color: "#64748b" }} />
                </div>
              </div>

              {activeTab === "General" && <GeneralTab email={userEmail} fullName={userName} organisation={organisation} number={userNumber} />}
              {activeTab === "Account" && <AccountTab email={userEmail} status={status} toolAccess={toolAccess} ticketAccess={ticketAccess} quotaPerPeriod={quotaPerPeriod} quotaRenewedAt={quotaRenewedAt} subscriptionId={subscriptionId} lastLogin={lastLogin} memberSince={memberSince} />}
              {activeTab === "Security" && <PrivacyTab />}
              {activeTab === "Billing" && (
                <BillingTab
                  subscription={subscription}
                  plan={plan}
                  wallet={wallet}
                  usage={usage}
                  creditBalanceCents={creditBalanceCents}
                  creditCurrency={creditCurrency}
                  paymentMethods={paymentMethods}
                  invoices={invoices}
                  userEmail={userEmail}
                  userName={userName}
                  userNumber={userNumber}
                  onRefreshBilling={onRefreshBilling}
                  onPaymentToast={onPaymentToast}
                />
              )}
              {activeTab === "Preferences" && <PreferencesTab />}
              {activeTab === "Usage" && <UsageTab plan={plan} wallet={wallet} usage={usage} />}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* -------- Tabs Content -------- */

/* -------- Usage Tab -------- */
const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const fmtTokens = (n: number) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n/1_000).toFixed(0)}K` : String(n);

const UsageTab = ({ plan, wallet, usage }: { plan?: any; wallet?: any; usage?: any }) => {
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("30d");
  const [periodData, setPeriodData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const periods = [{ label: "7d", value: "7d" }, { label: "30d", value: "30d" }, { label: "90d", value: "90d" }] as const;

  // Fetch period-specific data from /usage. The backend's aggregate doc only
  // tracks `current_month_tokens` and lifetime, so 7d/30d both return current
  // month, and 90d returns lifetime. Good enough for V1.
  React.useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/api/billing/usage?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { if (d.status === 'success') setPeriodData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  // ── Numbers ──
  // usedCredits      = tokens consumed in the selected period
  // totalCredits     = the user's monthly quota (plan.token_limit)
  //                    OR if no plan: lifetime budget = used + remaining
  // remainingWallet  = active tokens still spendable (across all buckets)
  const usedCredits     = periodData?.totalTokens ?? usage?.tokens_used ?? 0;
  const inputTokens     = periodData?.input_tokens  ?? usage?.input_tokens  ?? 0;
  const outputTokens    = periodData?.output_tokens ?? usage?.output_tokens ?? 0;
  const totalRequests   = periodData?.totalRequests ?? usage?.requests ?? 0;
  const remainingWallet = wallet?.total ?? 0;
  const totalCredits    = plan?.token_limit ?? (usedCredits + remainingWallet);
  const usedPct         = totalCredits > 0 ? Math.min(100, Math.round((usedCredits / totalCredits) * 100)) : 0;
  const days            = period === '7d' ? 7 : period === '30d' ? 30 : 90;
  const avgDaily        = Math.round(usedCredits / days);
  const bySource        = periodData?.by_source || {};

  const fmtN = (n: number) => fmtTokens(n);

  const kv = (label: string, value: string) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #d1d5db" }}>
      <span style={{ fontSize: "12px", color: "#64748b" }}>{label}</span>
      <span style={{ fontSize: "12px", fontWeight: 600, color: "#0f172a" }}>{value}</span>
    </div>
  );

  return (
    <div style={{ maxWidth: "100%", marginTop: "-8px" }}>

      {/* Header: period toggle */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <span style={{ fontSize: "12px", color: "#64748b" }}>Period</span>
        <div style={{ display: "flex", gap: "3px" }}>
          {periods.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              style={{
                padding: "3px 10px",
                background: period === p.value ? "#031735" : "#f1f5f9",
                color: period === p.value ? "white" : "#475569",
                border: "none", borderRadius: "4px",
                fontSize: "11px", fontWeight: 400, cursor: "pointer",
              }}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {/* 4-column stat strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1px", background: "#d1d5db", border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden", marginBottom: "10px" }}>
        {[
          { label: "Credits Used",    value: fmtN(usedCredits) },
          { label: "Quota",          value: totalCredits > 0 ? fmtN(totalCredits) : "—" },
          { label: "Total Requests", value: totalRequests.toLocaleString() },
          { label: "Avg / Day",      value: fmtN(avgDaily) },
        ].map(s => (
          <div key={s.label} style={{ background: "white", padding: "8px 10px" }}>
            <div style={{ fontSize: "10px", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "2px" }}>{s.label}</div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#0f172a" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Slim usage meter */}
      <div style={{ background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: "6px", padding: "7px 10px", marginBottom: "10px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
          <span style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b" }}>Credit Usage</span>
          <span style={{ fontSize: "12px", color: "#64748b" }}>
            {fmtN(usedCredits)} / {totalCredits > 0 ? fmtN(totalCredits) : "—"} · {usedPct}%
          </span>
        </div>
        <div style={{ height: "5px", background: "#d1d5db", borderRadius: "3px", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${usedPct}%`, background: usedPct > 80 ? "#ef4444" : "#031735", borderRadius: "3px" }} />
        </div>
      </div>

      {/* Summary key-value block */}
      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", padding: "7px 10px", marginBottom: "10px" }}>
        <div style={{ fontSize: "11px", color: "#1e293b", marginBottom: "4px", fontWeight: 600 }}>Period Summary</div>
        {kv("Credits used",    usedCredits.toLocaleString())}
        {kv("Input credits",   inputTokens.toLocaleString())}
        {kv("Output credits",  outputTokens.toLocaleString())}
        {kv("Wallet remaining", remainingWallet.toLocaleString())}
        {kv("Total requests",  totalRequests.toLocaleString())}
      </div>

      {/* Source breakdown — replaces the per-model table since we don't track per-model anymore */}
      <div style={{ border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", padding: "6px 10px", background: "#f9fafb", borderBottom: "1px solid #d1d5db" }}>
          {["Source", "Used (lifetime)", "Available now"].map(h => (
            <span key={h} style={{ fontSize: "10px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>{h}</span>
          ))}
        </div>
        {(() => {
          const sources = [
            { key: "subscription", label: "Plan",       color: "#031735" },
            { key: "addon",        label: "Top-up",     color: "#0ea5e9" },
            { key: "trial",        label: "Trial",      color: "#10b981" },
            { key: "promo",        label: "Promo",      color: "#a855f7" },
            { key: "admin_grant",  label: "Admin",      color: "#f59e0b" },
          ];
          const rows = sources
            .map(s => ({
              ...s,
              used:      bySource[s.key] || 0,
              available: wallet?.by_source?.[s.key] || 0,
            }))
            .filter(r => r.used > 0 || r.available > 0);

          if (loading) return (
            <div style={{ padding: "16px", textAlign: "center", fontSize: "12px", color: "#94a3b8" }}>Loading…</div>
          );
          if (rows.length === 0) return (
            <div style={{ padding: "16px", textAlign: "center", fontSize: "12px", color: "#94a3b8" }}>
              No usage yet. Send a chat message to start consuming credits.
            </div>
          );
          return rows.map((r, i) => (
            <div key={r.key} style={{
              display: "grid", gridTemplateColumns: "2fr 1fr 1fr",
              padding: "8px 10px",
              background: i % 2 === 0 ? "white" : "#f9fafb",
              borderBottom: i < rows.length - 1 ? "1px solid #d1d5db" : "none",
              alignItems: "center",
            }}>
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: r.color }} />
                {r.label}
              </span>
              <span style={{ fontSize: "12px", color: "#475569" }}>{r.used.toLocaleString()}</span>
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#0f172a" }}>{r.available.toLocaleString()}</span>
            </div>
          ));
        })()}
      </div>

    </div>
  );
};



const GeneralTab = ({ email, fullName, organisation, number }: { email?: string; fullName?: string; organisation?: string; number?: string }) => {
  const [role, setRole] = useState("Admin");

  return (
    <>
      <div style={{ marginBottom: "20px" }}>
        <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "12px" }}>Role Information</h3>

        <Field label="Email" value={email || "—"} />
        <Field label="Full Name" value={fullName || "—"} />
        <Field label="Organization" value={organisation || "—"} />
        {number && <Field label="Phone Number" value={number} />}

        <div style={{ marginBottom: "16px" }}>
          <label style={{
            display: "block",
            fontSize: "13px",
            fontWeight: 600,
            color: "#334155",
            marginBottom: "6px"
          }}>
            Role
          </label>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {[
              { value: "Admin", label: "Admin" },
              { value: "View", label: "View" },
              { value: "Manager", label: "Manager" }
            ].map((roleOption) => (
              <button
                key={roleOption.value}
                onClick={() => setRole(roleOption.value)}
                style={{
                  padding: "7px 14px",
                  borderRadius: "6px",
                  border: role === roleOption.value ? "2px solid #031735" : "1px solid #d1d5db",
                  backgroundColor: role === roleOption.value ? "#e8edf5" : "white",
                  color: role === roleOption.value ? "#031735" : "#64748b",
                  fontWeight: role === roleOption.value ? 600 : 500,
                  fontSize: "13px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                {roleOption.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

const AccountTab = ({
  email, status, toolAccess, ticketAccess, quotaPerPeriod, quotaRenewedAt, subscriptionId, lastLogin, memberSince
}: {
  email?: string; status?: string; toolAccess?: boolean; ticketAccess?: boolean;
  quotaPerPeriod?: number; quotaRenewedAt?: any; subscriptionId?: string | null;
  lastLogin?: any; memberSince?: any;
}) => {
  const fmt = (val: any) => {
    if (!val) return "—";
    try { return new Date(val?.seconds ? val.seconds * 1000 : val).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };
  const statusColor: Record<string, string> = { active: "#10b981", suspended: "#f59e0b", blocked: "#ef4444" };
  const st = status || "active";
  return (
    <>
      <Field label="Email" value={email || "—"} />
      <div style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
        <span style={{ fontSize: "13px", color: "#64748b", width: "160px", flexShrink: 0 }}>Account Status</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 500, color: statusColor[st] || "#64748b" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: statusColor[st] || "#94a3b8", display: "inline-block" }} />
          {st.charAt(0).toUpperCase() + st.slice(1)}
        </span>
      </div>
      <Field label="Member Since" value={fmt(memberSince)} />
      <Field label="Last Login" value={fmt(lastLogin)} />
      <Field label="Cases / Period" value={quotaPerPeriod != null ? String(quotaPerPeriod) : "10"} />
      <Field label="Quota Renewed" value={fmt(quotaRenewedAt)} />
      <div style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
        <span style={{ fontSize: "13px", color: "#64748b", width: "160px", flexShrink: 0 }}>Tool Access</span>
        <span style={{ fontSize: "13px", fontWeight: 500, color: toolAccess ? "#10b981" : "#94a3b8" }}>{toolAccess ? "Enabled" : "Disabled"}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
        <span style={{ fontSize: "13px", color: "#64748b", width: "160px", flexShrink: 0 }}>Ticket Access</span>
        <span style={{ fontSize: "13px", fontWeight: 500, color: ticketAccess ? "#10b981" : "#94a3b8" }}>{ticketAccess ? "Enabled" : "Disabled"}</span>
      </div>
      {subscriptionId && <Field label="Subscription ID" value={subscriptionId} />}
    </>
  );
};

const PrivacyTab = () => (
  <div style={{ maxWidth: "600px" }}>
    <div style={{ marginBottom: "20px" }}>
      <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>Passkeys</h3>
      <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "10px" }}>Secure authentication without passwords.</p>
      <button style={{
        padding: "7px 14px",
        backgroundColor: "#031735",
        color: "white",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: 600,
      }}>
        Add Passkey
      </button>
    </div>

    <div style={{ marginBottom: "20px" }}>
      <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>Two-Factor Authentication</h3>

      <div style={{ padding: "10px 12px", backgroundColor: "#f9fafb", border: "1px solid #d1d5db", borderRadius: "6px", marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "20px", height: "20px", borderRadius: "50%", backgroundColor: "#10b981", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px" }}>
            <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "white" }}></div>
          </div>
          <span style={{ fontSize: "13px", color: "#1e293b", fontWeight: 600 }}>Authenticator App</span>
          <span style={{ fontSize: "13px", color: "#64748b", marginLeft: "auto" }}>Enabled</span>
        </div>
      </div>

      <div style={{ padding: "10px 12px", backgroundColor: "#f9fafb", border: "1px solid #d1d5db", borderRadius: "6px", marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "20px", height: "20px", borderRadius: "50%", backgroundColor: "#d1d5db", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px" }}>
            <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#94a3b8" }}></div>
          </div>
          <span style={{ fontSize: "13px", color: "#1e293b", fontWeight: 600 }}>SMS Codes</span>
          <span style={{ fontSize: "13px", color: "#64748b", marginLeft: "auto" }}>Disabled</span>
        </div>
      </div>
    </div>

    <div>
      <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>Trusted Devices</h3>
      <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "10px" }}>Manage devices signed into your account.</p>

      <div style={{ padding: "10px 12px", backgroundColor: "#f9fafb", border: "1px solid #d1d5db", borderRadius: "6px", marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>Current Device</div>
            <div style={{ fontSize: "12px", color: "#64748b" }}>Browser on Windows</div>
          </div>
          <button style={{
            padding: "6px 12px",
            backgroundColor: "#f9fafb",
            color: "#475569",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
          }}>
            Sign Out
          </button>
        </div>
      </div>

      <button style={{
        padding: "7px 14px",
        backgroundColor: "#f9fafb",
        color: "#475569",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: 600,
      }}>
        Sign Out of All Devices
      </button>
    </div>
  </div>
);

const BillingTab = ({
  subscription, plan, wallet, usage, creditBalanceCents, creditCurrency,
  paymentMethods, invoices,
  userEmail, userName, userNumber, onRefreshBilling, onPaymentToast,
}: {
  subscription?: any; plan?: any; wallet?: any; usage?: any;
  creditBalanceCents?: number; creditCurrency?: string;
  paymentMethods?: any[]; invoices?: any[];
  userEmail?: string; userName?: string; userNumber?: string;
  onRefreshBilling?: () => void;
  onPaymentToast?: (toast: {
    kind: 'subscription' | 'addon' | 'cancelled' | 'failed';
    state: 'activating' | 'success' | 'cancelled' | 'failed';
    title: string;
    detail?: string;
  }) => void;
}) => {
  const [showBuyCreditsDialog, setShowBuyCreditsDialog] = useState(false);
  const [showBuyPlanDialog, setShowBuyPlanDialog] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming]     = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);

  const activePlan = plan || null;
  const activeSub  = subscription || null;
  const pmList     = paymentMethods || [];
  const invList    = invoices || [];
  const walletTotal = wallet?.total ?? 0;
  const bySource    = wallet?.by_source ?? {};

  // Refresh ALL billing data whenever this tab is mounted/re-rendered.
  // This is the industry-standard pattern: fresh data on tab open, no
  // dependency on whether webhooks already updated app-level state.
  const [refreshing, setRefreshing] = useState(false);
  const refreshAll = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    setRefreshing(true);
    try {
      const [h, p] = await Promise.all([
        fetch(`${API_BASE_URL}/api/billing/subscription-history`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
        fetch(`${API_BASE_URL}/api/billing/payments`,             { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      ]);
      if (h?.history)  setHistory(h.history);
      if (p?.payments) setPayments(p.payments);
      // Also refresh parent state (subscription / wallet / payment methods / invoices)
      onRefreshBilling?.();
    } catch (err) {
      console.warn("BillingTab refresh failed:", err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = async () => {
    if (!activeSub?.id) return;
    setCancelling(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_BASE_URL}/api/billing/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ subscriptionId: activeSub.id }),
      });
      if (!res.ok) throw new Error((await res.json())?.message || "Cancel failed");
      setShowCancelConfirm(false);
      // refreshAll picks up the new 'cancel_requested' history row + parent state
      refreshAll();
    } catch (err: any) {
      alert(err.message || "Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const handleResume = async () => {
    if (!activeSub?.id) return;
    setResuming(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_BASE_URL}/api/billing/resume`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ subscriptionId: activeSub.id }),
      });
      if (!res.ok) throw new Error((await res.json())?.message || "Resume failed");
      // refreshAll picks up the new 'reactivation' history row + parent state
      refreshAll();
    } catch (err: any) {
      alert(err.message || "Failed to resume subscription");
    } finally {
      setResuming(false);
    }
  };

  return (
    <div style={{ maxWidth: "100%", marginTop: "-8px" }}>

      {/* ── Wallet Banner — REAL token balance from token_buckets ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px", background: "#eff6ff",
        border: "1px solid #bfdbfe", borderRadius: "6px", marginBottom: "14px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "36px", height: "36px", borderRadius: "6px",
            background: "#031735", display: "flex", alignItems: "center",
            justifyContent: "center", flexShrink: 0,
          }}>
            <FontAwesomeIcon icon={faCreditCard} style={{ color: "white", fontSize: "14px" }} />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px" }}>Available Credits</div>
            <div style={{ fontSize: "22px", fontWeight: 800, color: "#031735", lineHeight: 1.2 }}>
              {walletTotal.toLocaleString()}
            </div>
            <div style={{ fontSize: "10px", color: "#64748b", marginTop: "2px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {bySource.subscription > 0 && <span>Plan: {bySource.subscription.toLocaleString()}</span>}
              {bySource.addon > 0 && <span>Addon: {bySource.addon.toLocaleString()}</span>}
              {bySource.trial > 0 && <span>Trial: {bySource.trial.toLocaleString()}</span>}
              {bySource.promo > 0 && <span>Promo: {bySource.promo.toLocaleString()}</span>}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            onClick={refreshAll}
            disabled={refreshing}
            title="Refresh billing data"
            style={{
              padding: "7px 10px", background: "transparent", color: "#64748b",
              border: "1px solid #d1d5db", borderRadius: "6px",
              cursor: refreshing ? "wait" : "pointer",
              fontSize: "12px", fontWeight: 600,
              display: "flex", alignItems: "center", justifyContent: "center",
              minWidth: "32px",
            }}
          >
            <span style={{
              display: "inline-block",
              transform: refreshing ? "rotate(360deg)" : "none",
              transition: refreshing ? "transform 0.8s linear" : "none",
              animation: refreshing ? "spin 0.8s linear infinite" : "none",
              fontSize: "13px",
            }}>↻</span>
          </button>
          <button
            onClick={() => setShowBuyCreditsDialog(true)}
            style={{
              padding: "7px 14px", background: "#031735", color: "white",
              border: "none", borderRadius: "6px", cursor: "pointer",
              fontSize: "12px", fontWeight: 600,
              display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: "11px" }} />
            Buy Credits
          </button>
          <button
            onClick={() => setShowBuyPlanDialog(true)}
            style={{
              padding: "7px 14px",
              background: activeSub ? "#eff6ff" : "white",
              color: "#031735",
              border: `1px solid ${activeSub ? "#60a5fa" : "#d1d5db"}`,
              borderRadius: "6px", cursor: "pointer",
              fontSize: "12px", fontWeight: 600,
            }}
          >
            {activeSub ? "Change Plan" : "Buy Plan"}
          </button>
        </div>
      </div>

      {/* ── Active Plan Card (REAL subscription) ── */}
      {activeSub && activePlan && (
        <div style={{
          display: "flex", alignItems: "center",
          padding: "12px 16px", background: "#eef2ff",
          border: "1.5px solid #031735", borderRadius: "12px",
          marginBottom: "14px", gap: "14px",
        }}>
          <div style={{
            width: "32px", height: "32px", borderRadius: "8px",
            background: "#031735", display: "flex", alignItems: "center",
            justifyContent: "center", flexShrink: 0,
          }}>
            <FontAwesomeIcon icon={faCreditCard} style={{ color: "white", fontSize: "13px" }} />
          </div>

          {/* Info column — name + badges row, then meta row */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>{activePlan.name}</span>
              <span style={{
                padding: "2px 8px",
                background: activeSub.status === "active"
                  ? "#dcfce7"
                  : activeSub.status === "past_due"
                  ? "#fef3c7"
                  : "#f1f5f9",
                color: activeSub.status === "active"
                  ? "#166534"
                  : activeSub.status === "past_due"
                  ? "#92400e"
                  : "#475569",
                fontSize: "10px", fontWeight: 600,
                borderRadius: "4px", lineHeight: "16px",
              }}>{activeSub.status === "active" ? "Active" : (activeSub.status || "")}</span>
              {activeSub.cancel_at_period_end && activeSub.current_period_end && (
                <span
                  title="Subscription will end on this date — click Resume to continue"
                  style={{
                    padding: "2px 8px", background: "#fef3c7", color: "#92400e",
                    fontSize: "10px", fontWeight: 600, borderRadius: "4px", lineHeight: "16px",
                  }}
                >Cancels {fmtDate(activeSub.current_period_end)}</span>
              )}
              {activeSub.pending_plan_id && !activeSub.cancel_at_period_end && activeSub.current_period_end && (
                <span
                  title="A downgrade is scheduled — your current plan stays active until this date"
                  style={{
                    padding: "2px 8px", background: "#dbeafe", color: "#1e40af",
                    fontSize: "10px", fontWeight: 600, borderRadius: "4px", lineHeight: "16px",
                  }}
                >Downgrade {fmtDate(activeSub.current_period_end)}</span>
              )}
            </div>
            <div style={{ fontSize: "12px", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {activePlan.token_limit?.toLocaleString?.() || "—"} credits / {activePlan.billing_cycle}
              {activeSub.current_period_end && !activeSub.cancel_at_period_end && ` • Renews ${fmtDate(activeSub.current_period_end)}`}
            </div>
          </div>

          {/* Price (compact) */}
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <span style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a" }}>{fmtRupees(activePlan.price)}</span>
            <span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 400 }}>
              /{activePlan.billing_cycle === "annual" ? "yr" : "mo"}
            </span>
          </div>

          {/* Buttons (compact, never wrap) */}
          <div style={{ display: "flex", gap: "6px", alignItems: "center", flexShrink: 0 }}>
            <button
              onClick={() => setShowBuyPlanDialog(true)}
              style={{
                padding: "5px 10px", background: "transparent", color: "#031735",
                border: "1.5px solid #031735", borderRadius: "6px",
                cursor: "pointer", fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap",
              }}
            >
              Change
            </button>
            {/* Three states: pending-cancel → Resume; idle → Cancel; mid-confirm → prompt */}
            {activeSub.cancel_at_period_end ? (
              <button
                onClick={handleResume}
                disabled={resuming}
                title="Continue your subscription past the next billing date"
                style={{
                  padding: "5px 10px",
                  background: resuming ? "#94a3b8" : "linear-gradient(135deg, #10b981, #059669)",
                  color: "white", border: "none", borderRadius: "6px",
                  cursor: resuming ? "not-allowed" : "pointer",
                  fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap",
                }}
              >
                {resuming ? "…" : "Resume"}
              </button>
            ) : !showCancelConfirm ? (
              <button
                onClick={() => setShowCancelConfirm(true)}
                style={{
                  padding: "5px 10px", background: "transparent", color: "#ef4444",
                  border: "1.5px solid #fca5a5", borderRadius: "6px",
                  cursor: "pointer", fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap",
                }}
              >
                Cancel
              </button>
            ) : (
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <span style={{ fontSize: "11px", color: "#64748b", whiteSpace: "nowrap" }}>Cancel subscription?</span>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  style={{
                    padding: "5px 12px", background: cancelling ? "#94a3b8" : "#ef4444",
                    color: "white", border: "none", borderRadius: "6px",
                    cursor: cancelling ? "not-allowed" : "pointer", fontSize: "11px", fontWeight: 700,
                  }}
                >
                  {cancelling ? "..." : "Confirm"}
                </button>
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  style={{
                    padding: "5px 12px", background: "#f1f5f9", color: "#475569",
                    border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "11px", fontWeight: 600,
                  }}
                >
                  Keep
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Credit balance indicator (after a downgrade or other proration) */}
      {(creditBalanceCents ?? 0) > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: "10px",
          padding: "10px 14px",
          background: "linear-gradient(135deg, #ecfdf5, #d1fae5)",
          border: "1px solid #6ee7b7", borderRadius: "8px",
          marginBottom: "12px",
        }}>
          <div style={{
            width: "30px", height: "30px", borderRadius: "50%",
            background: "#10b981", color: "white",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "14px", fontWeight: 800, flexShrink: 0,
          }}>$</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#065f46" }}>
              Account credit: {fmtMoney(creditBalanceCents, creditCurrency || "USD")}
            </div>
            <div style={{ fontSize: "11px", color: "#047857" }}>
              Will be applied automatically to your next invoice
              {activeSub?.current_period_end && ` on ${fmtDate(activeSub.current_period_end)}`}.
            </div>
          </div>
        </div>
      )}

      {/* Billing Information */}
      <div style={{ marginBottom: "12px" }}>
        <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>Billing Information</h3>
        <div style={{ padding: "10px", backgroundColor: "#f8fafc", borderRadius: "8px", marginBottom: "12px" }}>
          {activeSub ? (<>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ fontSize: "14px", color: "#64748b" }}>Next Billing Date</span>
              <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>{fmtDate(activeSub.current_period_end)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ fontSize: "14px", color: "#64748b" }}>Billing Cycle</span>
              <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>{activePlan?.billing_cycle ? activePlan.billing_cycle.charAt(0).toUpperCase() + activePlan.billing_cycle.slice(1) : "—"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ fontSize: "14px", color: "#64748b" }}>Amount</span>
              <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>{fmtRupees(activePlan?.price)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: "14px", color: "#64748b" }}>Auto-renew</span>
              <span style={{ fontSize: "14px", fontWeight: 600, color: activeSub.auto_renew ? "#10b981" : "#94a3b8" }}>
                {activeSub.auto_renew ? "On" : "Off"}
              </span>
            </div>
          </>) : (
            <div style={{ fontSize: "13px", color: "#94a3b8", textAlign: "center", padding: "8px 0" }}>
              No active subscription. {wallet?.by_source?.trial > 0 && `Trial credits: ${wallet.by_source.trial.toLocaleString()}`}
            </div>
          )}
        </div>
      </div>

      {/* Payment Methods */}
      <div style={{ marginBottom: "12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", margin: 0 }}>Payment Methods</h3>
        </div>
        {pmList.length === 0 ? (
          <div style={{ padding: "14px", backgroundColor: "#f8fafc", borderRadius: "8px", fontSize: "13px", color: "#94a3b8", textAlign: "center" }}>
            No payment methods saved — added automatically after your first successful payment.
          </div>
        ) : pmList.map((pm: any) => (
          <div key={pm.id} style={{ padding: "16px", backgroundColor: "#f8fafc", borderRadius: "8px", display: "flex", alignItems: "center", marginBottom: "8px" }}>
            <div style={{
              width: "48px", height: "32px", backgroundColor: "#031735", borderRadius: "6px",
              display: "flex", alignItems: "center", justifyContent: "center",
              marginRight: "12px", color: "white", fontSize: "12px", fontWeight: 700,
            }}>{(pm.brand || pm.type || "CARD").toUpperCase().slice(0, 4)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "14px", fontWeight: 500, color: "#1e293b" }}>•••• •••• •••• {pm.last4 || "••••"}</div>
              <div style={{ fontSize: "12px", color: "#64748b" }}>
                {pm.expiry_month ? `Expires ${pm.expiry_month.toString().padStart(2, "0")}/${String(pm.expiry_year ?? "").slice(-2)}` : pm.type?.toUpperCase()}
                {pm.is_default && " · Default"}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Invoices — split into Token / BYOIP / GPU sections */}
      {(() => {
        const renderInvoiceList = (list: any[]) => (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
            {list.length === 0 ? (
              <div style={{ padding: "20px", textAlign: "center", fontSize: "13px", color: "#94a3b8" }}>No invoices yet</div>
            ) : list.map((inv: any, index: number) => (
              <div key={inv.id || index} style={{
                padding: "14px 16px", backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                display: "flex", alignItems: "center",
                borderBottom: index < list.length - 1 ? "1px solid #e2e8f0" : "none",
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "14px", fontWeight: 500, color: "#1e293b" }}>
                    {fmtDate(inv.paid_at || inv.created_at)}
                    {inv.invoice_number && <span style={{ marginLeft: "8px", fontSize: "11px", color: "#94a3b8" }}>{inv.invoice_number}</span>}
                  </div>
                  {inv.description && <div style={{ fontSize: "11px", color: "#94a3b8" }}>{inv.description}</div>}
                </div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", marginRight: "16px" }}>{fmtRupees(inv.amount)}</div>
                <div style={{
                  padding: "4px 10px",
                  color: inv.status === "paid" ? "#10b981" : inv.status === "open" ? "#f59e0b" : "#94a3b8",
                  borderRadius: "20px", fontSize: "12px", fontWeight: 600, marginRight: "12px",
                }}>{inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1) : "—"}</div>
                {inv.invoice_url ? (
                  <a href={inv.invoice_url} target="_blank" rel="noreferrer" style={{
                    padding: "6px 12px", backgroundColor: "#f1f5f9", color: "#475569",
                    border: "none", borderRadius: "6px", fontSize: "12px", textDecoration: "none",
                  }}>Download</a>
                ) : (
                  <button disabled style={{
                    padding: "6px 12px", backgroundColor: "#f1f5f9", color: "#c4c4c4",
                    border: "none", borderRadius: "6px", cursor: "default", fontSize: "12px",
                  }}>Download</button>
                )}
              </div>
            ))}
          </div>
        );

        const byoipInvoices = invList.filter((inv: any) =>
          ['ip_marketplace_purchase', 'ip_marketplace_renewal'].includes(inv.type));
        const gpuInvoices = invList.filter((inv: any) => inv.type === 'gpu_purchase');
        // Catch-all: legacy invoices with no `type`, plus any future/unknown
        // type, land here rather than silently vanishing from all three lists.
        const tokenInvoices = invList.filter((inv: any) =>
          !byoipInvoices.includes(inv) && !gpuInvoices.includes(inv));

        return (
          <>
            <div style={{ marginBottom: "12px" }}>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>Token Purchases</h3>
              {renderInvoiceList(tokenInvoices)}
            </div>
            <div style={{ marginBottom: "12px" }}>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>BYOIP IP Leases</h3>
              {renderInvoiceList(byoipInvoices)}
            </div>
            <div style={{ marginBottom: "12px" }}>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>GPU Purchases</h3>
              {renderInvoiceList(gpuInvoices)}
            </div>
          </>
        );
      })()}

      {/* Subscription history (audit) */}
      {history.length > 0 && (
        <div style={{ marginBottom: "12px" }}>
          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>Subscription History</h3>
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
            {history.slice(0, 8).map((h: any, i: number) => (
              <div key={h.id || i} style={{
                padding: "10px 14px", backgroundColor: i % 2 === 0 ? "#ffffff" : "#f8fafc",
                display: "flex", alignItems: "center", gap: "10px",
                borderBottom: i < Math.min(history.length, 8) - 1 ? "1px solid #e2e8f0" : "none",
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "13px", color: "#1e293b", fontWeight: 600 }}>
                    {h.change_type?.replace(/_/g, " ")?.replace(/\b\w/g, (c: string) => c.toUpperCase()) || "—"}
                  </div>
                  {h.reason && <div style={{ fontSize: "11px", color: "#94a3b8" }}>{h.reason}</div>}
                </div>
                <div style={{ fontSize: "12px", color: "#64748b" }}>{fmtDate(h.changed_at)}</div>
                {h.amount_charged != null && (
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b", marginLeft: "8px" }}>{fmtRupees(h.amount_charged)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment history */}
      {payments.length > 0 && (
        <div>
          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>Payment History</h3>
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
            {payments.slice(0, 8).map((p: any, i: number) => (
              <div key={p.id || i} style={{
                padding: "10px 14px", backgroundColor: i % 2 === 0 ? "#ffffff" : "#f8fafc",
                display: "flex", alignItems: "center", gap: "10px",
                borderBottom: i < Math.min(payments.length, 8) - 1 ? "1px solid #e2e8f0" : "none",
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "13px", color: "#1e293b", fontWeight: 600 }}>
                    {p.method?.toUpperCase() || "CARD"} · {p.gateway_payment_id?.slice(0, 16)}
                  </div>
                  {p.failure_reason && <div style={{ fontSize: "11px", color: "#ef4444" }}>{p.failure_reason}</div>}
                </div>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b" }}>{fmtRupees(p.amount)}</div>
                <div style={{
                  padding: "3px 8px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
                  background: p.status === "captured" ? "#dcfce7"
                            : p.status === "failed"   ? "#fee2e2"
                            : "#f1f5f9",
                  color: p.status === "captured" ? "#166534"
                       : p.status === "failed"   ? "#991b1b"
                       : "#475569",
                }}>{p.status}</div>
                <div style={{ fontSize: "11px", color: "#64748b", minWidth: "70px", textAlign: "right" }}>
                  {fmtDate(p.created_at)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dialogs */}
      {showBuyCreditsDialog && (
        <AddCreditsDialog
          onClose={() => setShowBuyCreditsDialog(false)}
          userEmail={userEmail}
          userName={userName}
          userNumber={userNumber}
          // Same as BuyPlan below: use refreshAll so the local payments list
          // re-fetches alongside parent billing state.
          onSuccess={() => { setShowBuyCreditsDialog(false); refreshAll(); }}
        />
      )}
      {showBuyPlanDialog && (
        <BuyPlanDialog
          onClose={() => setShowBuyPlanDialog(false)}
          activePlanId={activeSub?.plan_id || null}
          activeSubscriptionId={activeSub?.id || null}
          userEmail={userEmail}
          userName={userName}
          userNumber={userNumber}
          // refreshAll re-fetches subscription_history + payments locally AND
          // calls onRefreshBilling for parent state (summary / PM / invoices).
          // Calling only onRefreshBilling left this tab's `history` state
          // stale after a plan change, so the new "Upgrade" row didn't show
          // until the user manually reloaded.
          onSuccess={() => { refreshAll(); }}
          onPaymentToast={onPaymentToast}
        />
      )}
    </div>
  );
};

/* -------- Add Credits Dialog (Anthropic-style) -------- */

const AddCreditsDialog: React.FC<{
  onClose: () => void;
  userEmail?: string;
  userName?: string;
  userNumber?: string;
  onSuccess: () => void;
}> = ({ onClose, userEmail, userName, userNumber, onSuccess }) => {
  const [packs, setPacks] = useState<any[]>([]);
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ tokens: number; rate_cents_per_1k: number; pricing_mode: string } | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load top-up packs from server (sorted by price ascending)
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/billing/plans?type=addon`)
      .then(r => r.json())
      .then(data => {
        const list = (data.plans || [])
          .filter((p: any) => p.type === "addon")
          .sort((a: any, b: any) => (a.price || 0) - (b.price || 0));
        setPacks(list);
        if (list.length && !selectedPackId) setSelectedPackId(list[0].id);
      })
      .catch(() => setError("Failed to load top-up packs"));
  }, []);

  const selected = packs.find(p => p.id === selectedPackId);

  // Live preview: how many tokens does the selected $ amount actually buy?
  // Depends on whether user has an active subscription (better rate) or not.
  useEffect(() => {
    if (!selected) { setPreview(null); return; }
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    fetch(`${API_BASE_URL}/api/billing/addons/preview?amount_cents=${selected.price}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d?.status === "success") {
          setPreview({ tokens: d.tokens, rate_cents_per_1k: d.rate_cents_per_1k, pricing_mode: d.pricing_mode });
        }
      })
      .catch(() => {});
  }, [selected?.id]);

  const subtotal = selected?.price ?? 0;     // cents

  const handleBuy = async () => {
    if (!selected) return;
    setProcessing(true);
    setError(null);
    try {
      const token = localStorage.getItem("auth_token");

      // Step 1: create Stripe Checkout Session (mode=payment) on backend
      const orderRes = await fetch(`${API_BASE_URL}/api/billing/addons/order`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ packId: selected.id }),
      });
      const orderData = await orderRes.json();
      if (!orderRes.ok) throw new Error(orderData?.message || "Order failed");
      if (!orderData?.checkout?.url) throw new Error("Stripe Checkout URL missing in response");

      // Step 2: redirect to Stripe — user comes back via success_url with ?session_id=...
      // The wallet refresh happens once App.tsx detects the redirect on landing.
      redirectToCheckout(orderData.checkout.url);
    } catch (e: any) {
      setError(e?.message || "Purchase failed");
      setProcessing(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)",
      display: "flex", justifyContent: "center", alignItems: "center",
      zIndex: 4000, backdropFilter: "blur(4px)", animation: "fadeIn 0.2s ease",
    }}>
      <div style={{
        background: "white", borderRadius: "16px", width: "460px", maxWidth: "95vw",
        boxShadow: "0 24px 48px rgba(0,0,0,0.2)", overflow: "hidden",
        animation: "zoomIn 0.2s ease",
      }}>
        {/* Header */}
        <div style={{ padding: "24px 24px 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ textAlign: "center", flex: 1 }}>
            <h2 style={{ margin: "0 0 4px", fontSize: "20px", fontWeight: 700, color: "#0f172a" }}>Buy credit pack</h2>
            <p style={{ margin: 0, fontSize: "13px", color: "#64748b", lineHeight: 1.5 }}>
              One-time purchase. Credits never expire and stack on top of your subscription.
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: "0", marginTop: "-4px" }}>
            <FontAwesomeIcon icon={faXmark} style={{ fontSize: "18px", color: "#94a3b8" }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px" }}>

          {/* Pricing-mode hint */}
          {preview && (
            <div style={{
              padding: "10px 12px", marginBottom: "14px",
              background: preview.pricing_mode === "subscription" ? "#dcfce7" : "#eff6ff",
              border: `1px solid ${preview.pricing_mode === "subscription" ? "#bbf7d0" : "#bfdbfe"}`,
              borderRadius: "8px", fontSize: "12px",
              color: preview.pricing_mode === "subscription" ? "#166534" : "#1e3a8a",
            }}>
              {preview.pricing_mode === "subscription" ? (
                <>
                  ✓ <strong>Subscriber rate:</strong> ${(preview.rate_cents_per_1k / 100).toFixed(2)} per 1K credits.
                  Top-ups stack on top of your monthly allowance and never expire.
                </>
              ) : (
                <>
                  💡 <strong>Pay-as-you-go:</strong> $5 ≈ 1,200 credits. Credits never expire.
                  Subscribe to a monthly plan for up to 25× more credits per dollar.
                </>
              )}
            </div>
          )}

          {/* Pack picker */}
          <div style={{ marginBottom: "18px" }}>
            <label style={{ display: "block", fontSize: "14px", fontWeight: 600, color: "#0f172a", marginBottom: "8px" }}>Choose an amount</label>
            {packs.length === 0 ? (
              <div style={{ padding: "14px", background: "#f8fafc", borderRadius: "8px", fontSize: "13px", color: "#94a3b8", textAlign: "center" }}>
                Loading…
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "8px" }}>
                {packs.map(pack => {
                  const isSel = selectedPackId === pack.id;
                  return (
                    <button
                      key={pack.id}
                      onClick={() => setSelectedPackId(pack.id)}
                      style={{
                        padding: "14px 6px",
                        background: isSel ? "#031735" : "white",
                        color: isSel ? "white" : "#0f172a",
                        border: `2px solid ${isSel ? "#031735" : "#e2e8f0"}`,
                        borderRadius: "10px", cursor: "pointer",
                        fontSize: "16px", fontWeight: 700,
                        textAlign: "center",
                      }}
                    >
                      {fmtMoney(pack.price, "USD")}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Computed tokens preview */}
          {selected && preview && (
            <div style={{
              padding: "14px 16px", marginBottom: "18px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0", borderRadius: "10px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontSize: "13px", color: "#64748b" }}>You'll receive</span>
                <span style={{ fontSize: "22px", fontWeight: 800, color: "#031735" }}>
                  {preview.tokens.toLocaleString()} <span style={{ fontSize: "13px", fontWeight: 500, color: "#64748b" }}>credits</span>
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px" }}>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>at ${(preview.rate_cents_per_1k / 100).toFixed(2)}/1K credits</span>
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>Never expires</span>
              </div>
            </div>
          )}

          {/* Total */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "baseline",
            paddingTop: "10px", borderTop: "1px solid #f1f5f9",
            marginBottom: "16px",
          }}>
            <span style={{ fontSize: "13px", color: "#0f172a", fontWeight: 700 }}>Total</span>
            <span style={{ fontSize: "16px", color: "#0f172a", fontWeight: 700 }}>{fmtMoney(subtotal, "USD")}</span>
          </div>

          {error && (
            <div style={{
              padding: "10px 12px", background: "#fee2e2", color: "#991b1b",
              borderRadius: "8px", fontSize: "12px", marginBottom: "10px",
            }}>{error}</div>
          )}

          {processing && (
            <div style={{ marginBottom: "12px" }}>
              <div style={{ height: "4px", background: "#e2e8f0", borderRadius: "2px", overflow: "hidden" }}>
                <div style={{
                  height: "100%", background: "#031735", borderRadius: "2px",
                  animation: "loadBar 1.8s ease-in-out infinite", width: "60%",
                }} />
              </div>
              <p style={{ textAlign: "center", fontSize: "12px", color: "#64748b", margin: "6px 0 0" }}>Redirecting to Stripe checkout…</p>
            </div>
          )}

          <button
            onClick={handleBuy}
            disabled={processing || !selected}
            style={{
              width: "100%", padding: "12px",
              background: (processing || !selected) ? "#94a3b8" : "#031735",
              color: "white", border: "none", borderRadius: "10px",
              fontSize: "15px", fontWeight: 700,
              cursor: (processing || !selected) ? "not-allowed" : "pointer",
            }}
          >
            {processing ? "Processing..." : `Pay ${fmtMoney(subtotal, "USD")}`}
          </button>

          <p style={{ textAlign: "center", fontSize: "11px", color: "#94a3b8", marginTop: "12px", lineHeight: 1.5 }}>
            Test card: <code>4242 4242 4242 4242</code>, any future expiry, any CVC.
          </p>
        </div>
      </div>
    </div>
  );
};

/* -------- Buy Plan Dialog -------- */

const BuyPlanDialog: React.FC<{
  onClose: () => void;
  activePlanId: string | null;
  activeSubscriptionId?: string | null;
  userEmail?: string;
  userName?: string;
  userNumber?: string;
  onSuccess: () => void;
  onPaymentToast?: (toast: {
    kind: 'subscription' | 'addon' | 'cancelled' | 'failed';
    state: 'activating' | 'success' | 'cancelled' | 'failed';
    title: string;
    detail?: string;
  }) => void;
}> = ({ onClose, activePlanId, activeSubscriptionId, userEmail, userName, userNumber, onSuccess, onPaymentToast }) => {
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(activePlanId || null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmInfo, setConfirmInfo] = useState<{ kind: 'upgrade' | 'downgrade'; effective_at?: string | null } | null>(null);

  // Load real subscription plans from server
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/billing/plans?type=subscription`)
      .then(r => r.json())
      .then(data => {
        const list = (data.plans || []).filter((p: any) => p.type !== "addon");
        setPlans(list);
        if (!selectedPlanId && list.length) {
          // Default to Growth (popular) if present, else first
          const popular = list.find((p: any) => p.tier === "growth") || list[0];
          setSelectedPlanId(popular.id);
        }
      })
      .catch(() => setError("Failed to load plans"));
  }, []);

  const selected = plans.find(p => p.id === selectedPlanId);

  const currentPlan = plans.find(p => p.id === activePlanId);
  // upgrade vs downgrade based on price comparison
  const intent: 'subscribe' | 'upgrade' | 'downgrade' = !activePlanId
    ? 'subscribe'
    : (selected && currentPlan && (selected.price || 0) > (currentPlan.price || 0))
      ? 'upgrade'
      : 'downgrade';

  const handleConfirm = async () => {
    if (!selected) return;
    setProcessing(true);
    setError(null);
    setConfirmInfo(null);
    try {
      const token = localStorage.getItem("auth_token");

      // ── New subscriber → Stripe Checkout (collects card) ──
      if (intent === 'subscribe') {
        const subRes = await fetch(`${API_BASE_URL}/api/billing/subscribe`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ planId: selected.id }),
        });
        const subData = await subRes.json();
        if (!subRes.ok) throw new Error(subData?.message || "Subscribe failed");
        if (!subData?.checkout?.url) throw new Error("Stripe Checkout URL missing in response");
        // Redirect → Stripe handles card collection → returns to ?billing=success
        redirectToCheckout(subData.checkout.url);
        return;
      }

      // ── Existing subscriber → /change-plan (uses saved card, no Checkout) ──
      if (!activeSubscriptionId) throw new Error("Missing active subscription id");
      const res = await fetch(`${API_BASE_URL}/api/billing/change-plan`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ subscriptionId: activeSubscriptionId, newPlanId: selected.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Plan change failed");

      const direction = data.direction as 'upgrade' | 'downgrade';
      const effectiveAt = data.effective_at as string | null | undefined;
      setConfirmInfo({ kind: direction, effective_at: effectiveAt });

      // App-level success toast — same green banner the user gets after the
      // initial subscribe. Differs by direction:
      //   • upgrade: "Upgraded to <plan>" + "<credits> credits added."
      //   • downgrade: "Downgrade scheduled" + "Takes effect <date>."
      if (onPaymentToast) {
        if (direction === 'upgrade') {
          const credits = selected.token_limit || selected.credits || selected.tokens || 0;
          onPaymentToast({
            kind: 'subscription',
            state: 'success',
            title: `Upgraded to ${selected.name || selected.tier || 'new plan'}`,
            detail: credits
              ? `${Number(credits).toLocaleString()} credits added.`
              : `Plan change effective immediately.`,
          });
        } else {
          // Downgrades take effect at period end — there's no immediate
          // credit grant, so frame the message accordingly.
          const niceDate = effectiveAt
            ? new Date(effectiveAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
            : 'next renewal';
          onPaymentToast({
            kind: 'subscription',
            state: 'success',
            title: `Downgrade scheduled`,
            detail: `Switches to ${selected.name || 'new plan'} on ${niceDate}.`,
          });
        }
      }

      // Refresh parent state so the active plan card updates. Stripe's
      // proration webhook (invoice.payment_succeeded / billing_reason=
      // subscription_update) lands a beat after this response — refetch
      // again at 1.5s and 4s so the new invoice + history row appear
      // without the user having to hit refresh.
      onSuccess();
      setTimeout(onSuccess, 1500);
      setTimeout(onSuccess, 4000);
    } catch (e: any) {
      setError(e?.message || "Plan change failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)",
      display: "flex", justifyContent: "center", alignItems: "center",
      zIndex: 3500, backdropFilter: "blur(4px)", animation: "fadeIn 0.2s ease",
    }}>
      <div style={{
        background: "white", borderRadius: "16px", width: "500px", maxWidth: "95vw",
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
        animation: "zoomIn 0.2s ease",
      }}>
        {/* Header */}
        <div style={{ padding: "24px 24px 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ textAlign: "center", flex: 1 }}>
            <h2 style={{ margin: "0 0 4px", fontSize: "20px", fontWeight: 700, color: "#0f172a" }}>
              {activePlanId ? "Change Plan" : "Buy Plan"}
            </h2>
            <p style={{ margin: 0, fontSize: "13px", color: "#64748b", lineHeight: 1.5 }}>
              {activePlanId ? "Switch to a different plan." : "Select a plan that works for your team."}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: "0", marginTop: "-4px" }}>
            <FontAwesomeIcon icon={faXmark} style={{ fontSize: "18px", color: "#94a3b8" }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px" }}>
          {/* Plan Cards */}
          {plans.length === 0 ? (
            <div style={{ padding: "14px", background: "#f8fafc", borderRadius: "8px", fontSize: "13px", color: "#94a3b8", textAlign: "center" }}>
              Loading…
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {plans.map((plan) => {
                const isSelected = selectedPlanId === plan.id;
                const isCurrent  = activePlanId === plan.id;
                return (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    style={{
                      background: "white",
                      borderRadius: "10px",
                      border: `2px solid ${isSelected ? "#031735" : "#e2e8f0"}`,
                      padding: "14px 16px",
                      display: "flex", alignItems: "center", gap: "12px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                        <span style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>{plan.name}</span>
                        {isCurrent && (
                          <span style={{ fontSize: "10px", fontWeight: 600, color: "#166534", background: "#dcfce7", padding: "2px 6px", borderRadius: "4px" }}>
                            Current
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>
                        {plan.token_limit?.toLocaleString()} credits / {plan.billing_cycle}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <span style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a" }}>{fmtRupees(plan.price)}</span>
                      <span style={{ fontSize: "12px", color: "#94a3b8", marginLeft: "2px" }}>/{plan.billing_cycle === "annual" ? "yr" : "mo"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {error && (
            <div style={{
              marginTop: "12px", padding: "10px 12px",
              background: "#fee2e2", color: "#991b1b",
              borderRadius: "8px", fontSize: "12px",
            }}>{error}</div>
          )}

          {confirmInfo && (
            <div style={{
              marginTop: "12px", padding: "12px 14px",
              background: "#dcfce7", color: "#166534",
              borderRadius: "8px", fontSize: "13px",
            }}>
              {confirmInfo.kind === "upgrade" ? (
                <>✓ <strong>Upgraded to {selected?.name}</strong>. The prorated diff has been charged to your card on file. New credits are available now.</>
              ) : (
                <>✓ <strong>Downgrade to {selected?.name} scheduled</strong>{confirmInfo.effective_at ? ` for ${fmtDate(confirmInfo.effective_at)}` : ''}. Your current plan and credits stay active until then; you'll be charged the new lower price on the next renewal.</>
              )}
            </div>
          )}

          {selected && selected.id !== activePlanId && !confirmInfo && (
            <button
              onClick={handleConfirm}
              disabled={processing}
              style={{
                width: "100%", marginTop: "16px", padding: "12px",
                background: processing ? "#94a3b8" : "#031735",
                color: "white", border: "none", borderRadius: "10px",
                fontSize: "15px", fontWeight: 700,
                cursor: processing ? "not-allowed" : "pointer",
              }}
            >
              {processing
                ? (intent === "subscribe" ? "Opening checkout…" : "Updating subscription…")
                : intent === "subscribe"
                  ? `Subscribe — ${selected.name} (${fmtRupees(selected.price)})`
                  : intent === "upgrade"
                    ? `Upgrade to ${selected.name} (${fmtRupees(selected.price)}/mo)`
                    : `Schedule downgrade to ${selected.name} (${fmtRupees(selected.price)}/mo)`}
            </button>
          )}

          {confirmInfo && (
            <button
              onClick={onClose}
              style={{
                width: "100%", marginTop: "12px", padding: "12px",
                background: "#031735", color: "white",
                border: "none", borderRadius: "10px",
                fontSize: "15px", fontWeight: 700, cursor: "pointer",
              }}
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PreferencesTab = () => {
  const [activeSubTab, setActiveSubTab] = useState("Users");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState("No access");
  const [users, setUsers] = useState([
    { name: "Rahul Chandyok", email: "rahul@devopsx.com", access: "Admin", lastLogin: "2 hours ago" },
    { name: "Priya Sharma", email: "priya@devopsx.com", access: "Read", lastLogin: "1 day ago" },
  ]);

  return (
    <div>
      {/* Sub Navigation */}
      <div style={{
        display: "flex",
        gap: "10px",
        marginBottom: "20px",
        borderBottom: "1px solid #d1d5db",
        paddingBottom: "10px"
      }}>
        <button
          onClick={() => setActiveSubTab("Users")}
          style={{
            padding: "7px 14px",
            backgroundColor: activeSubTab === "Users" ? "#031735" : "transparent",
            color: activeSubTab === "Users" ? "white" : "#64748b",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            transition: "all 0.2s ease",
          }}
        >
          <FontAwesomeIcon icon={faUsers} style={{ fontSize: "14px" }} />
          Users
        </button>
        <button
          onClick={() => setActiveSubTab("MAV")}
          style={{
            padding: "7px 14px",
            backgroundColor: activeSubTab === "MAV" ? "#031735" : "transparent",
            color: activeSubTab === "MAV" ? "white" : "#64748b",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            transition: "all 0.2s ease",
          }}
        >
          <FontAwesomeIcon icon={faFolderTree} style={{ fontSize: "14px" }} />
          Multi Account View
        </button>
      </div>

      {/* Users Tab Content */}
      {activeSubTab === "Users" && (
        <div>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "14px",
            padding: "10px 12px",
            backgroundColor: "#e8edf5",
            borderRadius: "6px",
            border: "1px solid #031735"
          }}>
            <p style={{ margin: 0, fontSize: "13px", color: "#031735" }}>
              Managing User Access and Roles: User Creation, Role Assignment, and Permissions Oversight.
            </p>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
            <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>Users List</h3>
            <button
              onClick={() => setShowAddUserModal(true)}
              style={{
                padding: "7px 14px",
                backgroundColor: "#031735",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              + Add New User
            </button>
          </div>

          {/* Users Table */}
          <div style={{ border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden" }}>
            {/* Table Header */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "2fr 2fr 1fr 1.5fr 1fr",
              backgroundColor: "#f9fafb",
              color: "#1e293b",
              padding: "10px 14px",
              fontSize: "12px",
              fontWeight: 700,
              borderBottom: "1px solid #d1d5db"
            }}>
              <div>Name</div>
              <div>Email ID</div>
              <div>Access</div>
              <div>Last Login</div>
              <div>Actions</div>
            </div>

            {/* Table Rows */}
            {users.length === 0 ? (
              <div style={{
                padding: "50px 20px",
                textAlign: "center",
                backgroundColor: "#ffffff"
              }}>
                <p style={{ margin: 0, fontSize: "14px", color: "#64748b", fontWeight: 600 }}>
                  No Data Available
                </p>
              </div>
            ) : (
              users.map((user, index) => (
                <div
                  key={index}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 2fr 1fr 1.5fr 1fr",
                    padding: "10px 14px",
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9fafb",
                    borderTop: "1px solid #d1d5db",
                    fontSize: "13px",
                    alignItems: "center"
                  }}
                >
                  <div style={{ fontWeight: 600, color: "#1e293b" }}>{user.name}</div>
                  <div style={{ color: "#64748b" }}>{user.email}</div>
                  <div>
                    <span style={{
                      padding: "3px 10px",
                      backgroundColor: user.access === "Admin" ? "#e8edf5" : "#e8edf5",
                      color: user.access === "Admin" ? "#031735" : "#031735",
                      borderRadius: "10px",
                      fontSize: "11px",
                      fontWeight: 700
                    }}>
                      {user.access}
                    </span>
                  </div>
                  <div style={{ color: "#64748b" }}>{user.lastLogin}</div>
                  <div>
                    <button style={{
                      padding: "5px 12px",
                      backgroundColor: "#f9fafb",
                      color: "#475569",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: 600
                    }}>
                      Edit
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* MAV Tab Content */}
      {activeSubTab === "MAV" && (
        <MAVContent />
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 3000
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "12px",
            width: "520px",
            maxHeight: "90vh",
            overflow: "auto"
          }}>
            {/* Modal Header */}
            <div style={{
              padding: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#1e293b" }}>
                Add User
              </h3>
              <button
                onClick={() => setShowAddUserModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px"
                }}
              >
                <FontAwesomeIcon icon={faXmark} style={{ fontSize: "20px", color: "#64748b" }} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                <div>
                  <label style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#1e293b",
                    marginBottom: "6px"
                  }}>
                    First Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    placeholder="Enter First Name"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "14px",
                      outline: "none"
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#1e293b",
                    marginBottom: "6px"
                  }}>
                    Last Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    placeholder="Enter Last Name"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "14px",
                      outline: "none"
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#1e293b",
                  marginBottom: "6px"
                }}>
                  Email ID <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  placeholder="Add Email ID"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    fontSize: "14px",
                    outline: "none"
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#1e293b",
                  marginBottom: "6px"
                }}>
                  Provide Access <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <select
                    value={selectedAccess}
                    onChange={(e) => setSelectedAccess(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "14px",
                      outline: "none",
                      cursor: "pointer",
                      backgroundColor: "white"
                    }}
                  >
                    <option value="No access">No access</option>
                    <option value="Admin">Admin</option>
                    <option value="Read">Read</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#1e293b",
                  marginBottom: "6px"
                }}>
                  Select MAV
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    fontSize: "14px",
                    outline: "none",
                    cursor: "pointer",
                    backgroundColor: "white"
                  }}
                >
                  <option>Select</option>
                  <option>Now.gg - dev-berlin</option>
                  <option>Production Account</option>
                </select>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: "16px 24px",
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px"
            }}>
              <button style={{
                padding: "8px 16px",
                backgroundColor: "white",
                color: "#64748b",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}>
                ↻ Reset
              </button>
              <button style={{
                padding: "8px 24px",
                backgroundColor: "#64748b",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 500
              }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MAVContent = () => {
  const [mavList] = useState([
    {
      name: "Now.gg - dev-berlin",
      accountIds: 1,
      assignedUsers: 0,
      isDefault: true
    }
  ]);
  const [selectedMAV, setSelectedMAV] = useState(mavList[0]);
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "20px" }}>
      {/* Left Panel - MAV List */}
      <div>
        <div style={{ marginBottom: "14px" }}>
          <input
            type="text"
            placeholder="Search MAVs"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "13px",
              outline: "none",
              fontFamily: "monospace"
            }}
          />
        </div>

        <div style={{ marginBottom: "10px" }}>
          <select style={{
            width: "100%",
            padding: "7px 10px",
            borderRadius: "6px",
            border: "1px solid #d1d5db",
            fontSize: "13px",
            outline: "none",
            cursor: "pointer",
            fontFamily: "monospace"
          }}>
            <option>All MAVs</option>
            <option>Default</option>
            <option>Custom</option>
          </select>
        </div>

        {/* MAV Cards */}
        {mavList.map((mav, index) => (
          <div
            key={index}
            onClick={() => setSelectedMAV(mav)}
            style={{
              padding: "12px",
              border: selectedMAV.name === mav.name ? "2px solid #3b82f6" : "1px solid #d1d5db",
              borderRadius: "6px",
              marginBottom: "8px",
              cursor: "pointer",
              backgroundColor: selectedMAV.name === mav.name ? "#eff6ff" : "white",
              position: "relative"
            }}
          >
            {mav.isDefault && (
              <span style={{
                position: "absolute",
                top: "8px",
                right: "8px",
                padding: "2px 8px",
                backgroundColor: "#dbeafe",
                color: "#1d4ed8",
                borderRadius: "8px",
                fontSize: "10px",
                fontWeight: 700
              }}>
                Default
              </span>
            )}
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", marginBottom: "7px" }}>
              {mav.name}
            </div>
            <div style={{ display: "flex", gap: "14px", fontSize: "11px", color: "#64748b" }}>
              <span>📋 Account IDs: {mav.accountIds}</span>
              <span>👥 Users: {mav.assignedUsers}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Right Panel - MAV Details */}
      <div>
        <div style={{ marginBottom: "14px" }}>
          <label style={{
            display: "block",
            fontSize: "13px",
            fontWeight: 700,
            color: "#1e293b",
            marginBottom: "7px"
          }}>
            MAV Name
          </label>
          <input
            value={selectedMAV.name}
            readOnly
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "13px",
              backgroundColor: "#f9fafb",
              outline: "none",
              fontFamily: "monospace"
            }}
          />
        </div>

        {selectedMAV.isDefault && (
          <div style={{
            padding: "10px 12px",
            backgroundColor: "#eff6ff",
            borderRadius: "6px",
            border: "1px solid #bfdbfe",
            marginBottom: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}>
            <span style={{ fontSize: "13px", color: "#1e40af", fontWeight: 600 }}>
              ℹ️ Modifying account IDs is not possible within default accounts.
            </span>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          {/* Choose Account IDs to Add */}
          <div>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px"
            }}>
              <label style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>
                Choose Accounts IDs to Add to MAV
              </label>
              <span style={{ fontSize: "12px", color: "#3b82f6", fontWeight: 600 }}>
                0 Added
              </span>
            </div>
            <div style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "16px",
              minHeight: "300px",
              backgroundColor: "white",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "48px", marginBottom: "12px" }}>📁</div>
                <div style={{ fontSize: "16px", fontWeight: 500, color: "#1e293b", marginBottom: "4px" }}>
                  No Account IDs to Add
                </div>
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  All Account IDs have been added to the MAV.
                </div>
              </div>
            </div>
          </div>

          {/* Account IDs added to MAV */}
          <div>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px"
            }}>
              <label style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>
                Account IDs added to MAV
              </label>
              <span style={{ fontSize: "12px", color: "#3b82f6", fontWeight: 600 }}>
                1 Added
              </span>
            </div>
            <input
              placeholder="Search"
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                marginBottom: "8px",
                outline: "none"
              }}
            />
            <div style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "12px",
              minHeight: "250px",
              backgroundColor: "white"
            }}>
              <div style={{
                padding: "8px 12px",
                backgroundColor: "#f8fafc",
                borderRadius: "6px",
                fontSize: "13px",
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <input type="checkbox" defaultChecked />
                dev-berlin(032760533965)
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          paddingTop: "16px",
          borderTop: "1px solid #e2e8f0"
        }}>
          <button style={{
            padding: "8px 16px",
            backgroundColor: "white",
            color: "#64748b",
            border: "1px solid #cbd5e1",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}>
            ↻ Reset
          </button>
          <button style={{
            padding: "8px 24px",
            backgroundColor: "#64748b",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500
          }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value }: any) => (
  <div style={{ marginBottom: "16px" }}>
    <label style={{
      display: "block",
      fontSize: "13px",
      fontWeight: 600,
      color: "#334155",
      marginBottom: "6px"
    }}>
      {label}
    </label>
    <input
      value={value}
      style={inputStyle}
      readOnly
    />
  </div>
);

/* -------- Styles -------- */

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.7)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 2000,
  backdropFilter: "blur(4px)",
  animation: "fadeIn 0.3s ease-out forwards",
  fontFamily: "monospace"
};

const modalStyle: React.CSSProperties = {
  width: "1000px",
  maxWidth: "95vw",
  height: "92vh",
  maxHeight: "90vh",
  background: "white",
  borderRadius: "8px",
  display: "flex",
  overflow: "hidden",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  animation: "zoomIn 0.3s ease-out forwards",
  fontFamily: "monospace"
};

const sidebarStyle: React.CSSProperties = {
  width: "240px",
  height: "100%",
  background: "#f9fafb",
  padding: "16px 12px",
  borderRight: "1px solid #d1d5db",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
};

const tabStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: "6px",
  cursor: "pointer",
  marginBottom: "3px",
  fontWeight: 400,
  transition: "all 0.2s ease",
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  padding: "12px 24px 24px 24px",
  position: "relative",
  background: "#ffffff",
  overflowY: "auto",
};

const itemStyle: React.CSSProperties = {
  padding: "10px 12px",
  cursor: "pointer",
  borderRadius: "6px",
  fontSize: "13px",
  display: "flex",
  alignItems: "center",
  color: "#334155",
  transition: "all 0.2s ease",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "360px",
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
  background: "#f9fafb",
  fontSize: "13px",
  color: "#334155",
  transition: "all 0.2s ease",
  outline: "none",
};

export default ProfileDropdown;